/**
 * Mega nav controller (CSS-first).
 *
 * Mouse open/close is handled entirely in CSS via :hover, with an
 * invisible ::before hover bridge on the panel covering the trigger-to-
 * panel gap. There are NO open/close timers: the menu opens instantly on
 * hover and closes the moment the pointer leaves both the trigger and the
 * panel.
 *
 * This module only owns keyboard and touch:
 * - Keyboard: focus entering an item opens its panel ([data-open]);
 *   focus leaving closes it; Escape closes and refocuses the trigger;
 *   arrow keys move across triggers and into/out of panels.
 * - Touch/pen: first tap opens, second tap follows the link; tapping
 *   outside closes.
 *
 * The [data-js] flag disables the CSS :focus-within fallback so keyboard
 * state is owned here (needed for a correct Escape). CSS :hover is NOT
 * gated by [data-js] — mouse works purely through CSS.
 */

const ITEM_SELECTOR = '[data-mega-nav-item]';
const PANEL_SELECTOR = ':scope > .mega-nav__panel';
const TRIGGER_SELECTOR = ':scope > .mega-nav__link';
const FOCUSABLE = 'a[href], button:not([disabled])';

class MegaNav {
  /** @param {HTMLElement} nav */
  constructor(nav) {
    this.nav = nav;
    /** @type {HTMLElement|null} */
    this.openItem = null;
    this.lastPointerType = 'mouse';

    /** Items that own a dropdown panel */
    this.panelItems = [...nav.querySelectorAll(ITEM_SELECTOR)].filter(
      (item) => item.querySelector(PANEL_SELECTOR)
    );

    /** All top-level links, for roving arrow-key navigation */
    this.topLinks = [...nav.querySelectorAll(ITEM_SELECTOR)]
      .map((item) => item.querySelector(TRIGGER_SELECTOR))
      .filter(Boolean);

    this.bind();

    // JS owns keyboard state; disable the CSS :focus-within fallback.
    nav.dataset.js = 'true';
  }

  bind() {
    for (const item of this.panelItems) {
      // Keyboard: open when focus enters from outside the item, close when
      // it leaves. Guarding on relatedTarget prevents Escape (which moves
      // focus back to the trigger, still inside the item) from re-opening.
      item.addEventListener('focusin', (event) => {
        if (item.contains(event.relatedTarget)) return;
        this.open(item);
      });

      item.addEventListener('focusout', (event) => {
        if (item.contains(event.relatedTarget)) return;
        this.close(item);
      });

      const trigger = item.querySelector(TRIGGER_SELECTOR);
      if (trigger) {
        // Touch / pen: first tap opens instead of navigating.
        trigger.addEventListener('click', (event) => {
          if (this.lastPointerType === 'mouse') return;
          if (this.openItem === item) return; // second tap: follow the link
          event.preventDefault();
          this.open(item);
        });
      }
    }

    this.nav.addEventListener('pointerdown', (event) => {
      this.lastPointerType = event.pointerType || 'mouse';
    });

    this.nav.addEventListener('keydown', (event) => this.onKeydown(event));

    // Tap/click anywhere outside closes the open panel (touch).
    this.onDocumentPointerDown = (event) => {
      if (!this.openItem) return;
      if (this.openItem.contains(event.target)) return;
      this.close(this.openItem);
    };
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
  }

  /** @param {HTMLElement} item */
  open(item) {
    if (this.openItem && this.openItem !== item) {
      this.setOpen(this.openItem, false);
    }
    if (this.openItem === item) return;
    this.setOpen(item, true);
    this.openItem = item;
  }

  /** @param {HTMLElement} item */
  close(item) {
    this.setOpen(item, false);
    if (this.openItem === item) this.openItem = null;
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} isOpen
   */
  setOpen(item, isOpen) {
    item.toggleAttribute('data-open', isOpen);
    const trigger = item.querySelector(TRIGGER_SELECTOR);
    if (trigger) trigger.setAttribute('aria-expanded', String(isOpen));
  }

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    const target = event.target;

    if (event.key === 'Escape') {
      const item = this.openItem;
      if (item) {
        const trigger = item.querySelector(TRIGGER_SELECTOR);
        this.close(item);
        if (trigger) trigger.focus();
        event.preventDefault();
      }
      return;
    }

    const isTopLink = this.topLinks.includes(target);

    if (isTopLink) {
      const index = this.topLinks.indexOf(target);
      const count = this.topLinks.length;

      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          this.topLinks[(index + 1) % count].focus();
          return;
        case 'ArrowLeft':
          event.preventDefault();
          this.topLinks[(index - 1 + count) % count].focus();
          return;
        case 'Home':
          event.preventDefault();
          this.topLinks[0].focus();
          return;
        case 'End':
          event.preventDefault();
          this.topLinks[count - 1].focus();
          return;
        case 'ArrowDown': {
          const item = target.closest(ITEM_SELECTOR);
          const panel = item ? item.querySelector(PANEL_SELECTOR) : null;
          if (item && panel) {
            event.preventDefault();
            this.open(item);
            const first = panel.querySelector(FOCUSABLE);
            if (first) first.focus();
          }
          return;
        }
        default:
          return;
      }
    }

    // Inside a panel: ArrowUp returns to the trigger.
    if (event.key === 'ArrowUp') {
      const item = target.closest(ITEM_SELECTOR);
      const panel = item ? item.querySelector(PANEL_SELECTOR) : null;
      if (item && panel && panel.contains(target)) {
        event.preventDefault();
        const trigger = item.querySelector(TRIGGER_SELECTOR);
        if (trigger) trigger.focus();
      }
    }
  }

  destroy() {
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
  }
}

const registry = new WeakSet();

function init(scope) {
  const root = scope || document;
  for (const nav of root.querySelectorAll('.mega-nav')) {
    if (registry.has(nav)) continue;
    registry.add(nav);
    new MegaNav(nav);
  }
}

init();

document.addEventListener('shopify:section:load', (event) => {
  init(event.target);
});
